export const meta = {
  id: '14-present-tense',
  order: 14,
  titleFi: 'Preesens',
  titleEn: 'Present tense',
  duolingoUnit: 'duo-15-present-tense',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['idakogon', 'līrigon', 'rȳbagon', 'ȳdragon', 'ours:davābvos', 'ours:kirennon', 'ours:gaomōbnon', 'ours:gaomenka', 'ours:tubāzma', 'ours:iēdruragon', 'ours:rāenābvos', 'ours:tistemvos', 'ours:ājurnegon', 'ours:jurnenon', 'ours:ipradiapos', 'ours:rȳvȳdranon', 'ours:gaomōbiapos', 'ours:lykāves', 'ours:kōtterion', 'ours:rūhākogon', 'ours:api', 'ours:filmi', 'ours:episodi', 'ours:seriali', 'ours:interneti', 'ours:strīmingi', 'ours:tubītsos', 'ours:emaili', 'ours:videa', 'ours:futboli', 'ours:faili', 'ours:dokumentari', 'ours:profili', 'ours:bingi', 'ours:tubiapos'],
  exampleIds: ['ex-decebd79e853', 'ex-baf1772f4b7d', 'hotd-s1e05-005', 'ex-006bcf7b325e', 'ex-1b0b3dd072a2', 'ex-bec327f6bf2e', 'got-s4e05-005', 'ex-e7241a89da5f', 'got-s5e03-005', 'hotd-s1e01-024'],
  checkFocus: { tense: ['present'] },   // what the end-of-lesson check tests first
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Present_tense',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Basics_2',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Verb_Conjugation',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_pronunciation',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['present'],
  grammarAlso: ['verb-classes'],
  showLines: ['hotd-s1e05-005', 'got-s4e05-005', 'got-s5e03-005', 'hotd-s1e01-024'],
  pronunciation: { focus: 'stress', words: ['idakogon', 'līrigon', 'rȳbagon', 'ȳdragon', 'atroksia'], lines: ['got-s4e05-005', 'got-s5e03-005'] },
  letter: {
    fi: 'Kerro viidellä lauseella, mitä teet tänään (preesens).',
    en: 'In five sentences, say what you are doing today (present).',
  },
  scene: 'got-s5e03-1',
}

# Preesens

Olla-verbin preesensin opit jo <LessonLink n={3}>oppitunnilla 3</LessonLink>. Muita verbejä on näkynyt alusta asti, mutta vain muutamassa persoonassa kerrallaan. Tällä oppitunnilla säännöllisten verbien preesens käydään läpi kokonaan, kaikki kuusi persoonaa.

Lähtökohta on suomalaiselle valmiiksi tuttu. Valyrian verbi mukautuu subjektiinsa persoonassa ja luvussa, ja koska pääte kertoo persoonan, pronominin voi jättää pois. <Hv glossed>Urnen</Hv> on yksinään ’näen’, aivan kuten suomessa. Uutta on vain se, että verbit jakautuvat kahteen päätyyppiin. Duolingon Tips & Notes -ohjeiden mukaan ratkaisevaa on, mihin verbin vartalo päättyy: vokaaliin vai konsonanttiin.

## Vokaalivartaloiset verbit

Vokaalivartaloisten verbien päätteet ovat melko säännölliset. Mallina on tuttu <Hv glossed>urnegon</Hv> ’nähdä’, jonka vartalo on <Hv>urne-</Hv>:

<Paradigm lemma="urnegon" filter="indicative.active.present" numbers="sg,pl" />

Duolingon ohjeet kuvaavat muodostuksen näin:

- ’minä’: vartalo + **-n**: <Seam s="urne" e="n" />
- ’sinä’: vartalon loppuvokaali pitenee: <Seam s="urn" e="ē" />
- ’hän’: vartalo + **-s**: <Seam s="urne" e="s" />
- ’me’: loppuvokaalin tilalle **-ī**: <Seam s="urn" e="ī" />
- ’te’: pidentynyt vokaali + **-t**: <Seam s="urn" e="ēt" />
- ’he’: vartalo + **-si**: <Seam s="urne" e="si" />

Yksikön 2. persoonan pitkä vokaali on aina vartalon oma vokaali. Siksi ’sinä hyökkäät’ on <Hv>idakō</Hv> (vartalo <Hv>idako-</Hv>), ’sinä hymyilet’ <Hv>līrī</Hv> (vartalo <Hv>līri-</Hv>) ja ’sinä nukut’ <Hv>ēdrū</Hv>.

<Pronounce word="idakogon" />

<Example id="ex-decebd79e853" />

<Interlinear hv="Azantys ohīlv|oso taob|e idako|s!" gloss="ritari-NOM tikari-INS poika-ACC hyökätä-PRS.3SG" fi="Ritari hyökkää pojan kimppuun tikarilla!" />

<Hv glossed>Ohīlvoso</Hv> on instrumentaalissa, ’tikarilla’. Tähän sijaan palataan myöhemmin omana aiheenaan.

### Missä suomi auttaa ja missä se hämää

Yksikön 1. persoonan -n on sama kuin suomessa:

<Compare fi="näe|n" hv="urne|n" label="yksikön 1. persoona: -n molemmissa" />

Pitkä vokaali persoonapäätteenä on sekin suomalaiselle tuttu, mutta eri persoonassa. Suomessa vokaali pitenee yksikön 3. persoonassa (*hän näkee, hän puhuu*), valyriassa yksikön 2. persoonassa:

<Compare fi="näe|t" hv="urn|ē" label="yksikön 2. persoona: suomessa -t, valyriassa pitkä vokaali" />

Kun siis kuulet valyriaksi pitkän vokaalin verbin lopussa, kyse on yleensä ’sinusta’ eikä ’hänestä’. Suomalainen vaisto ehdottaa toista, joten tätä kannattaa harjoitella erikseen. Yksi poikkeus on syytä tietää. Vokaalivartaloisten verbien ’me’-muodossakin on pitkä -ī (<Hv glossed>urnī</Hv> ’näemme’), joten i-vartaloisissa verbeissä ’sinä’- ja ’me’-muodot ovat samat: <Hv glossed>līrī</Hv> on sekä ’hymyilet’ että ’hymyilemme’. Silloin asiayhteys ratkaisee.

## Konsonanttivartaloiset verbit

Ohjeiden mukaan säännölliset konsonanttivartaloiset verbit saavat toisenlaiset päätteet. Mallina on <Hv glossed>rȳbagon</Hv> ’kuulla’, vartalo <Hv>rȳb-</Hv>:

<Paradigm lemma="rȳbagon" filter="indicative.active.present" numbers="sg,pl" />

- ’minä’ **-an**: <Hv>rȳban</Hv>
- ’sinä’ **-ā**: <Hv>rȳbā</Hv>
- ’hän’ **-as**: <Hv>rȳbas</Hv>
- ’me’ **-i**: <Hv>rȳbi</Hv>
- ’te’ **-āt**: <Hv>rȳbāt</Hv>
- ’he’ **-is**: <Hv>rȳbis</Hv>

<Example id="ex-baf1772f4b7d" />

Rhaenyra käyttää lauseen lopussa yksikön 1. persoonaa, ’niin kuulen’:

<Line id="hotd-s1e05-005" />

<Note>
**Soinniton vai soinnillinen?** Seuraava sääntö riippuu tästä erosta, joten Damien Halme (Academy of High Valyrian) käy sen läpi ennen <Term id="luokka">verbiluokkia</Term>. Pane sormet kurkulle ja sano pitkään *zzz* ja sitten *sss*. Soinnillisessa äänteessä kurkku värisee, soinnittomassa ei, vaikka suu on molemmissa samassa asennossa. Pareja ovat *p–b*, *t–d*, *k–g* ja *s–z*. Myös *l*, *r*, *m* ja *n* ovat soinnillisia.
</Note>

### Kolmas persoona vaihtelee

Konsonanttivartaloisissa verbeissä 3. persoonan pääte riippuu vartalon viimeisestä konsonantista. Ohjeet mainitsevat kaksi tärkeintä tapausta:

- **soinniton klusiili (p, t, k, q)**: yksikön 3. persoona on usein, mutta ei aina, **-sa**. Esimerkiksi <Hv glossed>majaqagon</Hv> ’ihailla’ → <Hv>majaqsa</Hv>.
- **r tai l**: yksikössä **-za**, monikossa **-zi**. Esimerkiksi <Hv glossed>jorrāelagon</Hv> ’rakastaa’ → <Hv>jorrāelza</Hv>, <Hv>jorrāelzi</Hv>.

<Note>
Damien Halme (Academy of High Valyrian) nimeää ryhmät yksikön 3. persoonan lopun mukaan: on **as-verbejä** ja **sa-verbejä**. <Hv glossed>Ipradas</Hv> ’hän syö’ on as-verbi, <Hv glossed>sōpsa</Hv> ’hän nauraa’ sa-verbi. Sa-verbeissä *s* muuttuu *l*:n ja *r*:n jälkeen *z*:ksi: <Hv glossed l="jaelagon">jaelza</Hv> ’hän haluaa’. Monikossa jako jatkuu: <Hv>ipradis</Hv>, mutta <Hv>sōpsi</Hv> ja <Hv l="jaelagon">jaelzi</Hv>. Uuden verbin ryhmä kannattaa silti tarkistaa sanakirjasta.
</Note>

Muiden konsonanttien jälkeen muutokset ovat epäsäännöllisempiä, ja ne opetellaan verbi kerrallaan. Wikin taivutussivulla on koko taulukko. Sen mukaan esimerkiksi <Term id="frikatiivi">frikatiivin</Term> ja konsonanttiyhtymän jälkeen tulee aina -as ja <Term id="soinnillinen">soinnillisen</Term> <Term id="klusiili">klusiilin</Term> jälkeen tavallisesti -as. Siksi yllä olevassa verbin <Hv>rȳbagon</Hv> taulukossa näkyvät myös rinnakkaismuodot <Hv>rȳbza</Hv> ja <Hv>rȳbzi</Hv>. Sivun mukaan ne ovat mahdollisia mutta eivät vakiomuotoja, joten käytä itse muotoja <Hv>rȳbas</Hv> ja <Hv>rȳbis</Hv>.

<Example id="ex-006bcf7b325e" />

<Example id="ex-1b0b3dd072a2" />

## Konsonantti vai a? ȳdragon ja rȳbagon

Ohjeet varoittavat vielä yhdestä ansasta. Osan verbeistä vartalo päättyy vokaaliin -a, ja niiden päätteet muistuttavat konsonanttivartaloisten verbien päätteitä niin paljon, että ne menevät helposti sekaisin. <Hv glossed>Ȳdran</Hv> ’puhun’ näyttää aivan samalta kuin <Hv>rȳban</Hv>. Monikon 3. persoona paljastaa eron: <Hv>ȳdrasi</Hv> voi olla vain a-vartaloisen verbin muoto, kun taas konsonanttivartaloinen verbi saisi muodon *ȳdris*.

<Paradigm lemma="ȳdragon" filter="indicative.active.present" numbers="sg,pl" />

Wikin taivutussivu lisää, ettei eroa näe infinitiivistäkään. <Hv>Ȳdragon</Hv> ja <Hv>rȳbagon</Hv> päättyvät kumpikin -agon. Käytännön neuvo on siis opetella uuden verbin mukana myös sen ’he’-muoto.

<Pronounce word="ȳdragon" />

<Example id="ex-bec327f6bf2e" />

<Interlinear hv="Gevī ȳdr|ā, azantys!" gloss="kauniisti puhua-PRS.2SG ritari-VOC" fi="Puhut kauniisti, ritari!" />

<Line id="got-s4e05-005" />

## Nyt vai aina?

Wikin taivutussivulla tehdään ero, jota Duolingon ohjeet eivät vielä mainitse. Valyrian preesens on ennen kaikkea sitä varten, mitä tapahtuu juuri nyt. Ajattomat totuudet ja tavat (’koirat haukkuvat’ yleisesti ottaen) ilmaistaan tavallisesti toisella aikamuodolla, aoristilla. Sanakirjan esimerkki näyttää eron:

<Example id="ex-e7241a89da5f" />

<Hv glossed>Rhovis</Hv> on preesens, ’haukkuvat (nyt)’, ja <Hv glossed>rhovisi</Hv> aoristi, ’haukkuvat (yleensä)’. Suomessa preesens hoitaa molemmat, joten tässä suomesta ei ole apua. Aoristi tulee myöhemmin, eikä sitä tarvitse vielä osata. Riittää, että tiedät, miksi sarjoissa kuulee joskus muodon, joka ei sovi tähän taulukkoon. Esimerkiksi punainen papitar sanoo <Hv glossed>Dārys rȳbis</Hv> ’hän kuulee kuninkaan’. Siinä <Hv glossed>rȳbis</Hv> ei ole preesensin monikko vaan aoristin yksikkö, ajaton ’kuulee’:

<Line id="got-s5e03-005" />

Samaan tapaan <Hv glossed>Rȳbās!</Hv> on käsky ’Tottele!’ eikä preesensin <Hv>rȳbā</Hv>. Myös käskymuodot tulevat myöhemmin.

<Line id="hotd-s1e01-024" />

<Practice hv="Taoba līris." fi="Poika hymyilee." />

<Practice hv="Līrī?" fi="Hymyiletkö? (tai: Hymyilemmekö?)" />

<Practice hv="Vali ȳdrasi." fi="Miehet puhuvat." />

<Practice hv="Rȳbāt?" fi="Kuuletteko?" />

<Practice hv="Idakī." fi="Hyökkäämme." />

<Note>
**Muotojen kuuntelukortit.** Tuttujen verbien preesensmuotoja on voinut tulla päivän korteissa vastaan kuuntelukortteina <LessonLink n={3}>oppitunnista 3</LessonLink> lähtien, ja nyt tuttuja verbejä on enemmän. Sano, mitä verbi tarkoittaa ja kuka tekee, esimerkiksi ”preesens, hän”.
</Note>

## Lopuksi

Vokaalivartaloisissa verbeissä päätteet ovat -n, pitkä vokaali, -s, -ī, pitkä vokaali + -t ja -si. Konsonanttivartaloisissa ne ovat -an, -ā, -as, -i, -āt ja -is, ja 3. persoona vaihtelee vartalon lopun mukaan. A-vartaloiset verbit tunnistaa ’he’-muodosta. Persoonat ovat suomalaiselle helppoja, mutta muista, että valyriassa pitkä vokaali kertoo ’sinusta’ eikä ’hänestä’.

<Note>
Jos persoonapääte on oikein mutta 3. persoonan muoto väärästä ryhmästä, tulet silti ymmärretyksi. Tästä muistuttaa Damien Halme (Academy of High Valyrian). Kirjoittaessa kannattaa olla tarkka, puhuessa tärkeintä on, että pääte vastaa tekijää.
</Note>

<Vocab />

<Check />
